import {
  Spread,
  LexicalNode,
  TextNode,
  SerializedTextNode,
  LexicalUpdateJSON,
  IS_SUPERSCRIPT,
  IS_SUBSCRIPT,
  $applyNodeReplacement,
  TextFormatType,
} from 'lexical';

export type SerializedReferenceCitationNode = Spread<{
  referenceId: string;
  mode: 'token';
}, Omit<SerializedTextNode, 'mode'>>;

// lexical does not export constant IS_TOKEN
export const REFERENCE_CITATION_IS_TOKEN = 1;

const SUPERSCRIPT = 'superscript';
const SUBSCRIPT = 'subscript';

export class ReferenceCitationNode extends TextNode {

  static fixFormat(format: number): number {
    return (format | IS_SUPERSCRIPT) & ~IS_SUBSCRIPT;
  }

  static isFormatTypeAllowed(format: TextFormatType): boolean {
    return format !== SUPERSCRIPT && format !== SUBSCRIPT;
  }

  static readonly TEXT_FOR_MISSING_NUMBER = '{r}';

  protected __referenceId: string;

  constructor(referenceId: string, referenceNumber?: string, key?: string) {
    super(referenceNumber ?? ReferenceCitationNode.TEXT_FOR_MISSING_NUMBER, key);

    this.__referenceId = referenceId;
    this.__mode = REFERENCE_CITATION_IS_TOKEN;

    this.__format = ReferenceCitationNode.fixFormat(this.__format);
  }

  static getType(): string {
    return 'reference-citation';
  }

  static clone(node: ReferenceCitationNode): ReferenceCitationNode {
    return new ReferenceCitationNode(node.__referenceId, node.__text, node.__key);
  }

  setNumber(value: number | undefined): this {
    const text = value && value > 0 ? value.toString() : ReferenceCitationNode.TEXT_FOR_MISSING_NUMBER;
    if (text !== this.getTextContent()) {
      this.setTextContent(text);
    }

    return this;
  }

  setHidden(): this {
    if (this.getTextContent()) {
      this.setTextContent('');
    }

    return this;
  }

  setMode(): this {
    // intentionally prevent change
    return this;
  }

  getReferenceId(): string {
    return this.getLatest().__referenceId;
  }

  setReferenceId(val: string): this {
    this.getWritable().__referenceId = val;

    return this;
  }

  setFormat(format: TextFormatType | number): this {
    if (typeof format === 'number') {
      return super.setFormat(ReferenceCitationNode.fixFormat(format));
    }

    return ReferenceCitationNode.isFormatTypeAllowed(format) ? super.setFormat(format) : this;
  }

  toggleFormat(format: TextFormatType): this {
    return ReferenceCitationNode.isFormatTypeAllowed(format) ? super.toggleFormat(format) : this;
  }

  static importJSON(
    serializedNode: SerializedReferenceCitationNode,
  ): ReferenceCitationNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createReferenceCitationNode(serializedNode.referenceId).updateFromJSON(serializedNode);
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<Omit<SerializedTextNode, 'mode'>>,
  ): this {
    this.setTextContent(serializedNode.text)
      .setDetail(serializedNode.detail)
      .setFormat(serializedNode.format)
      .setStyle(serializedNode.style);

    return this;
  }

  exportJSON(): SerializedReferenceCitationNode {
    const { mode, ...other } = super.exportJSON();

    return {
      ...other,
      mode: 'token',
      referenceId: this.getReferenceId(),
    };
  }
}

export function $createReferenceCitationNode(
  referenceId: string,
): ReferenceCitationNode {
  return $applyNodeReplacement(new ReferenceCitationNode(referenceId));
}

export function $isReferenceCitationNode(
  node: LexicalNode | null | undefined,
): node is ReferenceCitationNode {
  return node instanceof ReferenceCitationNode;
}
